import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Link from "@/v2-backoffice-client/components/link"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import UptimeMuteBadge from "@/v2-backoffice-client/uptime/mute/UptimeMuteBadge"
import UptimeLatencyBadge from "@/v2-backoffice-client/uptime/UptimeLatencyBadge"
import {
  classifyLatency,
  formatRelativeTime,
  formatUptimePercent,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import {
  getInlineStatusLabel,
  getInlineStatusVariant,
  pickInlineStatusKey,
} from "@/v2-backoffice-client/uptime/inline/uptime.inline.utils"
import type { UptimeInlineFragment } from "@/v2-backoffice-client/uptime/inline/__types__/uptime.inline.fragment"

// Whoever is rendering the summary points us at the right uptime
// view sheet. We never assume "maplayer:identifier" here so the same
// component can be reused for navlayer/basemap/app-* later without
// teaching it about each source's URL convention.
export type UptimeInlineSummaryProps = ComponentProps<"div"> & {
  target: UptimeInlineFragment | null | undefined
  loading?: boolean
  // Pre-built `to` href for the "View uptime details" link in the
  // card variant (the cell variant doesn't render a link). Pass a
  // string starting with `>>?` to push onto the view-sheet history
  // stack instead of replacing it.
  detailHref?: string
  // Compact for table cells, card for sheets/sidebars. Defaults to
  // "card" so a bare `<UptimeInlineSummary target={...} />` reads
  // well inside detail layouts.
  variant?: "card" | "cell"
}

function UptimeInlineSummary(props: Readonly<UptimeInlineSummaryProps>) {
  const {
    target,
    loading,
    detailHref,
    variant = "card",
    className,
    ...divProps
  } = props

  if (loading && !target) {
    return (
      <div
        className={cn(
          variant === "cell"
            ? "uptime-inline-summary-cell"
            : "uptime-inline-summary-card",
          "uptime-inline-summary-loading",
          className,
        )}
        {...divProps}
      >
        <span className="text-muted-foreground/70 text-xs">Loading…</span>
      </div>
    )
  }

  if (!target) {
    // No monitor configured for this layer (the etl filters out
    // layers with no probeable URL). We render an explicit empty
    // state rather than nothing so it's clear *why* the row is
    // blank.
    return (
      <div
        className={cn(
          variant === "cell"
            ? "uptime-inline-summary-cell"
            : "uptime-inline-summary-card",
          "uptime-inline-summary-empty",
          className,
        )}
        {...divProps}
      >
        <Dot variant="muted" />
        <span className="uptime-inline-summary-empty-text">
          {variant === "cell" ? "—" : "No uptime monitor"}
        </span>
      </div>
    )
  }

  const statusKey = pickInlineStatusKey(target)
  const statusLabel = getInlineStatusLabel(statusKey)
  const statusVariant = getInlineStatusVariant(statusKey)

  const latencyBaseline =
    target.P95DurationMs90d ?? target.AvgDurationMs90d ?? null
  const latencyClass = classifyLatency(target.AvgDurationMs24h, latencyBaseline)

  if (variant === "cell") {
    return (
      <div
        className={cn("uptime-inline-summary-cell", className)}
        title={`${statusLabel} • ${formatUptimePercent(target.UptimePercent24h)} · 24h`}
        {...divProps}
      >
        <Dot variant={statusVariant} />
        <span className="uptime-inline-summary-cell-uptime tabular-nums">
          {formatUptimePercent(target.UptimePercent24h)}
        </span>
        {target.AvgDurationMs24h != null && (
          <UptimeLatencyBadge
            currentMs={target.AvgDurationMs24h}
            baselineMs={latencyBaseline}
            variant={latencyClass}
            className="uptime-inline-summary-cell-latency"
          />
        )}
        {target.IsMuted && <UptimeMuteBadge target={target} />}
      </div>
    )
  }

  return (
    <section
      className={cn("uptime-inline-summary-card", className)}
      {...divProps}
    >
      <header className="uptime-inline-summary-card-header">
        <div className="uptime-inline-summary-card-title">
          <Dot variant={statusVariant} />
          <span className="uptime-inline-summary-card-title-text">
            Uptime · {statusLabel}
          </span>
          {target.IsMuted && <UptimeMuteBadge target={target} />}
        </div>
        {detailHref && (
          <Link
            to={detailHref}
            iconSrc={null}
            variant="muted"
            size="xs"
            className="uptime-inline-summary-card-link"
          >
            View uptime details
            <Icon src="arrow-right" size={12} className="ml-1" />
          </Link>
        )}
      </header>

      <div className="uptime-inline-summary-card-stats">
        <Stat
          label="Uptime · 24h"
          value={formatUptimePercent(target.UptimePercent24h)}
          sub="last 24 hours"
        />
        <Stat
          label="Uptime · 7d"
          value={formatUptimePercent(target.UptimePercent7d)}
          sub="last 7 days"
        />
        <Stat
          label="Uptime · 90d"
          value={formatUptimePercent(target.UptimePercent90d)}
          sub="last 90 days"
        />
        <Stat
          label="Avg · 24h"
          value={
            target.AvgDurationMs24h == null ? (
              <span className="text-muted-foreground">—</span>
            ) : (
              <UptimeLatencyBadge
                currentMs={target.AvgDurationMs24h}
                baselineMs={latencyBaseline}
                variant={latencyClass}
              />
            )
          }
          sub={
            target.IsSlowOutlier
              ? "≥2× baseline · slow outlier"
              : "rolling mean of probes"
          }
        />
        <Stat
          label="p95 · 90d"
          value={
            target.P95DurationMs90d == null ? (
              <span className="text-muted-foreground">—</span>
            ) : (
              <UptimeLatencyBadge
                currentMs={target.P95DurationMs90d}
                variant="normal"
              />
            )
          }
          sub="95% of probes ≤ this"
        />
      </div>

      <footer className="uptime-inline-summary-card-meta">
        Last checked {formatRelativeTime(target.LastCheckedAt)}
        {target.LastDurationMs != null && <> · {target.LastDurationMs} ms</>}
      </footer>
    </section>
  )
}

type StatProps = {
  label: string
  value: React.ReactNode
  sub?: string
}

function Stat(props: Readonly<StatProps>) {
  return (
    <div className="uptime-inline-summary-card-stat">
      <div className="uptime-inline-summary-card-stat-label">{props.label}</div>
      <div className="uptime-inline-summary-card-stat-value">{props.value}</div>
      {props.sub && (
        <div className="uptime-inline-summary-card-stat-sub">{props.sub}</div>
      )}
    </div>
  )
}

export default UptimeInlineSummary
